import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase.ts';
import emailjs from '@emailjs/browser';

emailjs.init(import.meta.env.VITE_EMAILJS_PUBLIC_KEY);

import { Menu, X, ChevronDown, ShoppingBag, ArrowRight, Zap, Eye, MessageCircle, Check, Settings } from 'lucide-react';
import { VideoPlayer } from '../components/VideoPlayer.tsx';
import Team from '../components/Team.tsx';
import Clients from '../components/Clients.tsx';
import Location from '../components/Location.tsx';
import SilentImage from '../components/SilentImage';

interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  image_url: string;
  image_url2?: string;
  image_url3?: string;
  image_url4?: string;
  is_premium: boolean;
  display_order: number;
}

interface Category {
  id: string;
  name: string;
  description: string;
  image_url?: string;
}

export default function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [content, setContent] = useState<Record<string, string>>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [detailImageLoading, setDetailImageLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  const [productImageIndexes, setProductImageIndexes] = useState<Record<string, number>>({});


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_type: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    loadProducts();
    loadCategories();
    fetchContent();
    // Subscribe to realtime updates from site_content to reflect admin edits immediately
    const channel = supabase
      .channel('site_content_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'site_content' },
        () => {
          fetchContent();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products' },
        () => {
          loadProducts();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'categories' },
        () => {
          loadCategories();
        }
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        // no-op
      }
    };
  }, []);

  // Carrusel automático
  useEffect(() => {
    if (!selectedProduct || getAllImages(selectedProduct).length <= 1) return;

    const interval = setInterval(() => {
      if (!isPaused) {
        setActiveImageIndex((prev) => (prev + 1) % getAllImages(selectedProduct).length);
      }
    }, 3000); // Cambia cada 3 segundos

    return () => clearInterval(interval);
  }, [selectedProduct, isPaused]);

  useEffect(() => {
    if (!selectedProduct) return;
    setDetailImageLoading(true);
  }, [selectedProduct, activeImageIndex]);

  useEffect(() => {
    if (!selectedProduct) return;
    const images = getAllImages(selectedProduct);
    if (images.length <= 1) return;

    const nextIndex = (activeImageIndex + 1) % images.length;
    const prevIndex = (activeImageIndex - 1 + images.length) % images.length;

    const preload = (src: string) => {
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
      } catch {
        // no-op
      }
    };

    preload(images[nextIndex]);
    preload(images[prevIndex]);
  }, [selectedProduct, activeImageIndex]);

  // Carrusel automático para tarjetas de productos
  useEffect(() => {
    if (!showProductsModal || !selectedCategory || products.length === 0) return;

    const interval = setInterval(() => {
      setProductImageIndexes((prev) => {
        const newIndexes: Record<string, number> = { ...prev };

        products
          .filter((p) => p.category === selectedCategory)
          .forEach((product) => {
            const images = getAllImages(product);
            if (images.length > 1) {
              const currentIndex = prev[product.id] || 0;
              newIndexes[product.id] = (currentIndex + 1) % images.length;
            }
          });

        return newIndexes;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [products, selectedCategory, showProductsModal]);

  const fetchContent = async () => {
    try {
      const { data } = await supabase
        .from('site_content')
        .select('key, value');

      if (data && data.length > 0) {
        const contentData: Record<string, string> = {};
        data.forEach((item: { key: string; value: string }) => {
          contentData[item.key] = item.value;
        });
        setContent(contentData);
      } else {
        // If no data, keep empty
        setContent({});
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      // Keep empty on error
      setContent({});
    }
  };

  const loadProducts = async () => {
    // Carga productos con nombre de categoría
    const { data } = await supabase
      .from('products')
      .select(`
        *,
        categories (
          name
        )
      `)
      .eq('is_visible', true)
      .order('display_order', { ascending: true });

    if (data) {
      // Adaptar claves comunes si vienen con otros nombres
      const mapped = data.map((p: Record<string, unknown>) => {
        const pp = p as {
          id?: string;
          identificacion?: string;
          'identificación'?: string;
          name?: string;
          nombre?: string;
          description?: string;
          descripcion?: string;
          'descripción'?: string;
          categories?: { name?: string };
          category?: string;
          category_name?: string;
          price?: number;
          image_url?: string;
          imagen?: string;
          imagen_url?: string;
          image_url2?: string;
          image_url3?: string;
          image_url4?: string;
          is_premium?: boolean;
          premium?: boolean;
          display_order?: number;
        };

        return {
          id: pp.id || pp.identificacion || pp['identificación'] || crypto.randomUUID?.() || String(Math.random()),
          name: pp.name || pp.nombre || '',
          description: pp.description || pp['descripción'] || pp.descripcion || '',
          category: pp.categories?.name || pp.category || pp.category_name || '',
          price: pp.price ?? 0,
          image_url: pp.image_url || pp.imagen || pp.imagen_url || '',
          image_url2: pp.image_url2 || '',
          image_url3: pp.image_url3 || '',
          image_url4: pp.image_url4 || '',
          is_premium: Boolean(pp.is_premium ?? pp.premium ?? false),
          display_order: pp.display_order ?? 0,
        };
      }) as Product[];
      setProducts(mapped);
    }
  };

  const loadCategories = async () => {
    const { data } = await supabase
      .from('categories')
      .select('*')
      .eq('is_visible', true)
      .order('name');

    if (data) {
      const mapped = data.map((c: Record<string, unknown>) => {
        const cc = c as {
          id: string;
          name: string;
          description?: string;
          image_url?: string;
          logo_url?: string;
          imagen_url?: string;
          imagen?: string;
        };

        return {
          id: cc.id,
          name: cc.name,
          description: cc.description || '',
          image_url: cc.image_url || cc.logo_url || cc.imagen_url || cc.imagen || '',
        };
      }) as Category[];
      setCategories(mapped);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage('');

    try {
      // Insertar en la tabla existente "contacts"
      const { error } = await supabase
        .from('contacts')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            business_type: formData.business_type,
            message: formData.message,
            status: 'pending',
          },
        ]);

if (error) throw error;

      setSubmitMessage('¡Gracias por contactarnos! Nuestro equipo se pondra en contacto en menos de 24 horas.');

      // Enviar correos con EmailJS
      const emailParams = {
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
        service_type: formData.business_type,
        products: '',
        message: formData.message,
        year: new Date().getFullYear()
      };

      try {
        const userEmailParams = {
          ...emailParams,
          to_email: formData.email
        };
        await emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          import.meta.env.VITE_EMAILJS_USER_TEMPLATE_ID,
          userEmailParams,
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        );
      } catch (emailErr) {
        console.error('Error email usuario:', emailErr);
      }

      try {
        await emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          import.meta.env.VITE_EMAILJS_COMPANY_TEMPLATE_ID,
          emailParams,
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        );
      } catch (emailErr) {
        console.error('Error email empresa:', emailErr);
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        business_type: '',
        message: '',
      });
    } catch {
      setSubmitMessage('Hubo un error. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Funciones auxiliares para el carrusel de imágenes
  const getAllImages = (product: Product) => {
    const images = [product.image_url];
    if (product.image_url2) images.push(product.image_url2);
    if (product.image_url3) images.push(product.image_url3);
    if (product.image_url4) images.push(product.image_url4);
    return images.filter(img => img && img.trim() !== '');
  };

  const getCurrentImage = (product: Product, index: number) => {
    const images = getAllImages(product);
    return images[index] || product.image_url;
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-md sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <img src="/logo.png" alt="Mundo Carne" className="h-12 w-auto" />

            {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-8 items-center">
                <a href="#inicio" className="text-gray-700 hover:text-red-600 transition">Inicio</a>
                 <a href="#empresa" className="text-gray-700 hover:text-red-600 transition">Empresa</a>
                 <a href="#contacto" className="text-gray-700 hover:text-red-600 transition">Contacto</a>
                <Link
                  to="/admin/login"
                  className="text-gray-500 hover:text-red-600 transition p-2 rounded-full hover:bg-red-50"
                  title="Panel de Administración"
                >
                  <Settings className="w-5 h-5" />
                </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <Link
                to="/admin/login"
                className="text-gray-500 hover:text-red-600 transition p-2 rounded-full hover:bg-red-50"
                title="Panel de Administración"
              >
                <Settings className="w-5 h-5" />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4 pt-4">
                <a
                  href="#inicio"
                  className="text-gray-700 hover:text-red-600 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Inicio
                </a>
                <a
                  href="#empresa"
                  className="text-gray-700 hover:text-red-600 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Empresa
                </a>
                <a
                  href="#contacto"
                  className="text-gray-700 hover:text-red-600 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contacto
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      <section
        id="inicio"
        className="relative bg-cover bg-center py-32"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?q=80&w=1200&h=600&auto=format&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-red-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-5xl font-bold mb-6 drop-shadow-lg">
            {content['Título de héroe']}
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto drop-shadow-md">
            {content['Subtítulo de héroe']}
          </p>
          <button
            type="button"
            onClick={() => setShowProductsModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition shadow-xl"
          >
            {content['Llamada a héroe']}
          </button>
        </div>
      </section>

      {/* Video Section */}
      {content['video_url'] && (
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-full shadow-2xl mb-8">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                <span className="font-bold text-base">Video Institucional</span>
              </div>

              <h2 className="text-5xl md:text-6xl font-black text-white mb-8 tracking-tight">
                {content['Título del video'] || 'Conoce Nuestra Historia'}
              </h2>

              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
                {content['Descripción del video'] || 'Descubre más sobre nuestros procesos, calidad y compromiso con la satisfacción de nuestros clientes.'}
              </p>
            </div>

            <div className="flex justify-center">
              <VideoPlayer
                videoUrl={content['video_url']}
                title={content['Título del video'] || "Video Institucional"}
                description={content['Descripción del video'] || "Conoce más sobre Mundo Carne"}
              />
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-400 text-sm">
                🎬 Video promocional • Haz clic para activar el sonido
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Por Qué Elegirnos
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
              <h4 className="text-xl font-bold text-red-600 mb-4">{content['Título del Servicio de Carnicería']}</h4>
              <p className="text-gray-700">{content['Servicio de Carnicería Desc']}</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
              <h4 className="text-xl font-bold text-red-600 mb-4">{content['Título del restaurante de servicio']}</h4>
              <p className="text-gray-700">{content['Servicio Restaurante Desc']}</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-600">
              <h4 className="text-xl font-bold text-red-600 mb-4">{content['Título del negocio de servicio']}</h4>
              <p className="text-gray-700">{content['Descripción del negocio de servicio']}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-100 rounded-full blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Nuestros Productos
            </h2>

            <p className="text-lg md:text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
              Descubre nuestra exclusiva selección de carnes premium al vacío, cuidadosamente seleccionadas
              para ofrecer la máxima calidad y sabor a nuestros clientes más exigentes.
            </p>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setShowProductsModal(true)}
                className="group relative inline-flex items-center justify-center gap-4 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-bold py-5 px-12 rounded-2xl text-lg transition-all duration-300 shadow-2xl hover:shadow-3xl"
              >
                <Menu className="w-6 h-6 opacity-90" />
                <span>Ver Catálogo de Productos</span>
                <ChevronDown className="w-6 h-6 opacity-90" />
                <div className="absolute -left-6 top-1/2 -translate-y-1/2 w-3 h-3 bg-red-300 rounded-full opacity-70"></div>
                <div className="absolute -right-6 top-1/2 -translate-y-1/2 w-3 h-3 bg-red-300 rounded-full opacity-70"></div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Clients />

      <section id="empresa" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">{content['Acerca del título']}</h3>
          <div className="bg-gray-50 rounded-xl p-10 shadow-md">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {content['Acerca del contenido 1']}
            </p>
            <h4 className="text-2xl font-bold text-red-600 mt-8 mb-4">Garantía, Experiencia y Compromiso</h4>
            <p className="text-gray-700 mb-6 leading-relaxed">
              {content['Acerca del contenido 2']}
            </p>
            <h4 className="text-2xl font-bold text-red-600 mt-8 mb-4">Gestión Profesional y Transparente</h4>
            <p className="text-gray-700 leading-relaxed">
              {content['Acerca del contenido 3']}
            </p>
            <p className="text-gray-700 leading-relaxed mt-6">
              {content['Acerca del contenido 4']}
            </p>
          </div>
        </div>
      </section>


      <section id="contacto" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-4 text-gray-900">{content['Título del contacto']}</h3>
          <p className="text-center text-gray-600 mb-8">
            {content['Subtítulo de contacto']}
          </p>

          {/* Shipping Restrictions Notice */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl p-6 mb-8 shadow-xl border-l-4 border-yellow-400">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h4 className="text-xl font-bold uppercase tracking-wider">Importante</h4>
            </div>
            <p className="text-center text-lg font-semibold">
              🚚 DESPACHO SOLO EN SANTIAGO Y MÍNIMO 5 CAJAS
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitMessage && (
                <div className={`p-4 rounded-lg ${submitMessage.includes('Gracias') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {submitMessage}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre Completo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Negocio</label>
                <select
                  value={formData.business_type}
                  onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccione una opción</option>
                  <option value="carniceria">Carnicería</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="hotel">Hotel</option>
                  <option value="fuente_soda">Fuente de Soda</option>
                  <option value="distribuidor">Distribuidor</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Cuéntenos sobre sus necesidades..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-6 rounded-lg transition disabled:opacity-50"
              >
                {submitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Team Section - Después de Contacto */}
      <Team />

      {/* Location Section */}
      <Location
        title={content['location_title'] || 'Dónde Estamos'}
        description={content['location_description'] || 'Visítanos en nuestra ubicación principal'}
        images={[
          content['location_image_1'],
          content['location_image_2'],
          content['location_image_3'],
          content['location_image_4']
        ].filter(img => img && img.trim() !== '')}
        mapUrl={content['location_map_url'] || ''}
        address={content['location_address'] || content['footer_address'] || ''}
      />

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold mb-4">{content['footer_company_name'] || content['Nombre de la empresa en el pie de página']}</h3>
              {(content['footer_description'] || content['Descripción del pie de página']) && (
                <p className="text-gray-300 mb-4">{content['footer_description'] || content['Descripción del pie de página']}</p>
              )}
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              {(content['footer_address'] || content['Dirección del pie de página']) && (
                <p className="text-gray-300 mb-2">{content['footer_address'] || content['Dirección del pie de página']}</p>
              )}
              {(content['footer_phone'] || content['Pie de página Teléfono']) && (
                <p className="text-gray-300 mb-2">{content['footer_phone'] || content['Pie de página Teléfono']}</p>
              )}
              {(content['footer_email'] || content['Correo electrónico del pie de página']) && (
                <p className="text-gray-300 mb-2">{content['footer_email'] || content['Correo electrónico del pie de página']}</p>
              )}
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Horarios de Atención</h4>
              {(content['footer_hours'] || content['Horario del pie de página']) && (
                <p className="text-gray-300 whitespace-pre-line">{content['footer_hours'] || content['Horario del pie de página']}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              {content['Pie de página Derechos de autor']}
            </p>
          </div>
        </div>
      </footer>

      {/* Products Modal */}
      {showProductsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center">
                {selectedCategory && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategory(null)}
                    className="mr-4 text-gray-500 hover:text-gray-700"
                  >
                    ← Volver
                  </button>
                )}
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedCategory ? `Productos - ${selectedCategory}` : content['Título del producto']}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowProductsModal(false);
                  setSelectedCategory(null);
                  setSelectedProduct(null);
                  setActiveImageIndex(0);
                  setIsPaused(false);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              {!selectedCategory ? (
                <>
                  <div className="bg-gradient-to-br from-rose-50 via-white to-red-50 rounded-3xl border border-rose-100 shadow-xl overflow-hidden">
                    <div className="p-8">
                      <div className="flex items-center justify-between gap-6 flex-col md:flex-row">
                        <div className="flex items-center gap-4">
                          <div className="bg-red-600 rounded-2xl p-4 shadow-lg">
                            <ShoppingBag className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="text-3xl font-bold text-gray-900">Nuestras Categorías</h3>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-red-600">{categories.length}</div>
                            <div className="text-sm font-semibold text-gray-500">Categorías</div>
                          </div>
                          <div className="h-10 w-px bg-gray-200" />
                          <div className="text-center">
                            <div className="text-3xl font-bold text-gray-700">{products.length}</div>
                            <div className="text-sm font-semibold text-gray-500">Productos</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-10 grid md:grid-cols-3 gap-8">
                        {categories.map((category) => {
                          const productCount = products.filter((p) => p.category === category.name).length;
                          const imageUrl = category.image_url && category.image_url.trim() !== '' ? category.image_url : null;

                          return (
                            <div
                              key={category.id}
                              onClick={() => setSelectedCategory(category.name)}
                              className="group bg-white/70 backdrop-blur rounded-2xl p-6 shadow-lg hover:shadow-2xl transition cursor-pointer border border-white hover:border-red-200"
                            >
                              <div className="flex items-center gap-4">
                                <div className="relative flex-shrink-0">
                                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md border border-gray-100 flex items-center justify-center overflow-hidden">
                                    {imageUrl ? (
                                      <img src={imageUrl} alt={category.name} className="w-full h-full object-contain p-2" />
                                    ) : (
                                      <span className="text-3xl">🥩</span>
                                    )}
                                  </div>
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
                                </div>

                                <div className="flex-1">
                                  <div className="text-xl font-bold text-gray-900">{category.name}</div>
                                  <div className="mt-2 inline-flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1 rounded-full text-sm font-bold">
                                    <span>{productCount} productos</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCategory(category.name);
                                  }}
                                  className="flex-shrink-0 w-11 h-11 rounded-2xl bg-red-600 text-white shadow-lg flex items-center justify-center group-hover:translate-x-0.5 transition"
                                  aria-label={`Ver productos de ${category.name}`}
                                >
                                  <ArrowRight className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-rose-100 px-8 py-5 flex items-center justify-between gap-4 flex-col md:flex-row">
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">Tip profesional:</span> Selecciona una categoría para ver nuestros productos premium
                      </div>
                      <div className="text-sm font-bold text-red-600 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        <span>Calidad garantizada</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-center text-gray-600 mb-8">
                    {content['Subtítulo de productos']}
                  </p>
                  <div className="grid md:grid-cols-3 gap-6">
                    {products.filter(product => product.category === selectedCategory).map((product) => {
                      const images = getAllImages(product);
                      const currentImageIndex = productImageIndexes[product.id] || 0;
                      const currentImage = images[currentImageIndex] || product.image_url;
                      
                      return (
                        <div
                          key={product.id}
                          className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition cursor-pointer"
                          style={{ touchAction: 'manipulation' }}
                          onClick={() => setSelectedProduct(product)}
                        >
                           <div className="relative bg-gradient-to-br from-gray-100 to-gray-200">
                              <SilentImage
                                src={currentImage}
                                alt={product.name}
                                className="w-full h-48 object-cover opacity-95"
                                loading="lazy"
                                decoding="async"
                                fetchpriority="low"
                              />

                            <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {images.length} fotos
                            </div>

                            {images.length > 1 && (
                              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                                {images.map((_, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setProductImageIndexes((prev) => ({
                                        ...prev,
                                        [product.id]: idx,
                                      }));
                                    }}
                                    className={`h-1.5 rounded-full transition-all ${
                                      idx === currentImageIndex ? 'w-6 bg-white' : 'w-2 bg-white/60 hover:bg-white/80'
                                    }`}
                                    aria-label={`Ver foto ${idx + 1} de ${product.name}`}
                                  />
                                ))}
                              </div>
                            )}

                            <div className="absolute bottom-3 right-3 text-xs text-white/80">
                              Click para ampliar
                            </div>

                            {product.is_premium && (
                              <span className="absolute top-3 right-3 bg-red-600 text-white px-2.5 py-1 rounded-full text-xs font-bold">
                                Premium
                              </span>
                            )}
                          </div>

                          <div className="p-5">
                            <h4 className="text-lg font-extrabold text-gray-900 mb-1">{product.name}</h4>
                            <div className="text-sm text-gray-500 mb-3">Marca {selectedCategory}</div>

                            <div className="flex items-center justify-between gap-4">
                              <div className="inline-flex items-center bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-bold">
                                {selectedCategory}
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProduct(product);
                                  }}
                                  className="w-10 h-10 rounded-xl bg-gray-700 text-white flex items-center justify-center hover:bg-gray-800 transition"
                                  aria-label={`Ver detalles de ${product.name}`}
                                >
                                  <Eye className="w-5 h-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowProductsModal(false);
                                    setSelectedCategory(null);
                                    document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
                                  }}
                                  className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition"
                                  aria-label={`Cotizar ${product.name}`}
                                >
                                  <MessageCircle className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-8 bg-gray-50 rounded-xl p-6 text-center">
                    <h4 className="text-xl font-bold text-red-600 mb-3">{content['Productos de vacío Título']}</h4>
                    <p className="text-gray-700">
                      {content['Productos de vacío Descripción']}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative bg-white rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
            <button
              type="button"
              onClick={() => {
                setSelectedProduct(null);
                setActiveImageIndex(0);
                setIsPaused(false);
              }}
              className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:bg-gray-50"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: gallery */}
              <div className="bg-slate-900 p-6 lg:p-8">
                 <div
                   className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 aspect-square"
                   onMouseEnter={() => setIsPaused(true)}
                   onMouseLeave={() => setIsPaused(false)}
                 >
                   {detailImageLoading && (
                     <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                       <div className="w-16 h-16 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
                     </div>
                   )}
                    <SilentImage
                      src={getCurrentImage(selectedProduct, activeImageIndex) || ''}
                      alt={`${selectedProduct.name} - Imagen ${activeImageIndex + 1}`}
                      className="w-full h-full object-cover"
                      decoding="async"
                      loading="lazy"
                      fetchpriority="high"
                      onLoad={() => setDetailImageLoading(false)}
                      onError={() => setDetailImageLoading(false)}
                    />

                  <div className="absolute top-4 right-4 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-bold">
                    {activeImageIndex + 1}/{getAllImages(selectedProduct).length}
                  </div>

                  {getAllImages(selectedProduct).length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setActiveImageIndex(
                            (prev) => (prev - 1 + getAllImages(selectedProduct).length) % getAllImages(selectedProduct).length
                          )
                        }
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
                        aria-label="Imagen anterior"
                      >
                        <span className="text-2xl leading-none">‹</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveImageIndex((prev) => (prev + 1) % getAllImages(selectedProduct).length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
                        aria-label="Siguiente imagen"
                      >
                        <span className="text-2xl leading-none">›</span>
                      </button>
                    </>
                  )}

                  {getAllImages(selectedProduct).length > 1 && (
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
                      {getAllImages(selectedProduct).map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveImageIndex(idx)}
                          className={`h-2 rounded-full transition-all ${
                            idx === activeImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
                          }`}
                          aria-label={`Ir a imagen ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: info */}
              <div className="p-8 lg:p-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm font-bold">
                  {selectedProduct.category}
                </div>

                <h3 className="mt-4 text-5xl font-black text-gray-900 tracking-tight">
                  {selectedProduct.name}
                </h3>

                <div className="mt-6">
                  <h4 className="text-xl font-extrabold text-gray-900">Descripción del Producto</h4>
                  <div className="mt-3 text-gray-700 leading-relaxed">{selectedProduct.description}</div>
                  <div className="mt-3 text-gray-500">Marca {selectedProduct.category}</div>
                </div>

                <div className="mt-10">
                  <h4 className="text-xl font-extrabold text-gray-900">Características Principales</h4>
                  <div className="mt-5 space-y-4">
                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-extrabold text-gray-900">Empaque Premium</div>
                        <div className="text-gray-600">Empaque al vacío de máxima calidad para garantizar frescura y conservación prolongada.</div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-extrabold text-gray-900">Calidad Certificada</div>
                        <div className="text-gray-600">Productos con certificación de calidad y origen garantizado, cumpliendo los más altos estándares.</div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-extrabold text-gray-900">Trazabilidad Completa</div>
                        <div className="text-gray-600">Control total desde el origen hasta la entrega, garantizando transparencia y confianza en cada producto.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 bg-red-50 rounded-2xl p-6 border border-red-100">
                  <h4 className="text-xl font-extrabold text-gray-900">Ventajas para tu Negocio</h4>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm font-semibold text-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span>Entrega inmediata</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span>Envíos nacionales</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span>Garantía 100%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span>Asesoría personalizada</span>
                    </div>
                  </div>
                </div>

                <div className="mt-10 space-y-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProduct(null);
                      setActiveImageIndex(0);
                      setIsPaused(false);
                      setShowProductsModal(false);
                      setSelectedCategory(null);
                      document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-extrabold py-5 rounded-2xl text-lg shadow-xl"
                  >
                    Solicitar Cotización Ahora
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProduct(null);
                      setActiveImageIndex(0);
                      setIsPaused(false);
                    }}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-5 rounded-2xl text-lg"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
